import { Layers, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { Badge, Button, Input, MOD_PREFIX, Textarea } from "@ncp/ui";
import { DEFAULT_LANES } from "../graph";
import { useBuilder, useStore } from "../store";
import type { GraphIssue } from "../types";
import { EdgeInspector } from "./EdgeInspector";
import { NodeInspector } from "./NodeInspector";
import { Group, Mini, Row } from "./parts";

/** Right panel: the selected state or transition, or the workflow's own settings when nothing is selected. */
export function Inspector({ editable, issues, tab, onTab, repoKey }: { editable: boolean; issues: GraphIssue[]; tab: string; onTab: (t: string) => void; repoKey: string }) {
  const graph = useBuilder((s) => s.graph);
  const selection = useBuilder((s) => s.selection);
  const node = selection.nodes.length === 1 ? graph.nodes.find((n) => n.id === selection.nodes[0]) : undefined;
  const edge = !node && selection.edges.length === 1 ? graph.edges.find((e) => e.id === selection.edges[0]) : undefined;
  return (
    <aside className="flex h-full w-[360px] shrink-0 flex-col border-s border-sand-200 bg-white/90 backdrop-blur">
      {node ? <NodeInspector key={node.id} node={node} graph={graph} issues={issues.filter((i) => i.node === node.key && !i.edge)} editable={editable} tab={tab} onTab={onTab} repoKey={repoKey} />
        : edge ? <EdgeInspector key={edge.id} edge={edge} graph={graph} issues={issues} editable={editable} tab={tab} onTab={onTab} />
        : selection.nodes.length > 1 ? <MultiSelection count={selection.nodes.length} editable={editable} />
        : <WorkflowSettings editable={editable} issues={issues} />}
    </aside>
  );
}

function MultiSelection({ count, editable }: { count: number; editable: boolean }) {
  const { t } = useTranslation();
  const store = useStore();
  const selection = useBuilder((s) => s.selection);
  return (
    <div className="space-y-3 p-4 text-sm">
      <p className="font-semibold">{t("admin:builder.inspector.selected", { count })}</p>
      <p className="text-xs text-muted-foreground">{t("admin:builder.inspector.multiHint", { mod: MOD_PREFIX })}</p>
      {editable ? <Button size="sm" variant="outline" className="text-danger-700" onClick={() => { store.update("Delete", (g) => { const keys = new Set(g.nodes.filter((n) => selection.nodes.includes(n.id!) && n.key !== g.initialState).map((n) => n.key)); g.nodes = g.nodes.filter((n) => !keys.has(n.key)); g.edges = g.edges.filter((e) => !keys.has(e.from) && !keys.has(e.to)); }); store.select({ nodes: [], edges: [] }); }}><Trash2 /> {t("admin:builder.inspector.deleteSelected")}</Button> : null}
    </div>
  );
}

function WorkflowSettings({ editable, issues }: { editable: boolean; issues: GraphIssue[] }) {
  const { t } = useTranslation();
  const store = useStore();
  const meta = useBuilder((s) => s.meta);
  const graph = useBuilder((s) => s.graph);
  const d = !editable;
  const lanes = graph.lanes?.length ? graph.lanes : DEFAULT_LANES;
  const errors = issues.filter((i) => i.severity === "Error").length;
  const warnings = issues.filter((i) => i.severity === "Warning").length;
  const setMeta = (label: string, fn: (m: typeof meta) => void, field: string) => store.update(label, (_, m) => fn(m), `meta:${field}`);
  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-sand-200 p-3"><p className="text-sm font-semibold">{t("admin:builder.inspector.workflow")}</p><p className="text-[11px] text-muted-foreground">{t("admin:builder.inspector.workflowHint")}</p></div>
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-3 text-sm">
        <Group title={t("admin:builder.inspector.identity")}>
          <Mini label="Name (EN)"><Input disabled={d} value={meta.name} onChange={(e) => setMeta("Name", (m) => { m.name = e.target.value; }, "name")} dir="ltr" /></Mini>
          <Mini label="الاسم (AR)"><Input disabled={d} value={meta.nameAr} onChange={(e) => setMeta("Name", (m) => { m.nameAr = e.target.value; }, "nameAr")} dir="rtl" /></Mini>
          <Mini label={t("admin:builder.inspector.description")}><Textarea disabled={d} rows={3} value={meta.description ?? ""} onChange={(e) => setMeta("Description", (m) => { m.description = e.target.value || null; }, "desc")} dir="auto" /></Mini>
          <Mini label={t("admin:builder.inspector.legalBasis")}><Input disabled={d} value={meta.legalBasis ?? ""} onChange={(e) => setMeta("Legal basis", (m) => { m.legalBasis = e.target.value || null; }, "legal")} dir="auto" /></Mini>
          <p className="text-[11px] text-muted-foreground">{t("admin:builder.inspector.caseType")}: <Badge size="sm" variant="outline">{meta.caseType}</Badge></p>
        </Group>
        <Group title={<><Layers className="me-1 inline size-3" />{t("admin:builder.inspector.lanes")}</>} hint={t("admin:builder.inspector.lanesHint")}
          action={!d ? <Button size="sm" variant="ghost" onClick={() => store.update("Lanes", (g) => { g.lanes = [...lanes.map((l) => ({ ...l })), { index: lanes.length, label: `Lane ${lanes.length + 1}`, labelAr: null }]; })}><Plus /></Button> : null}>
          {lanes.map((l, i) => (
            <Row key={l.index}>
              <Input disabled={d} className="h-8 text-xs" value={l.label} onChange={(e) => store.update("Lanes", (g) => { g.lanes = lanes.map((x, k) => (k === i ? { ...x, label: e.target.value } : { ...x })); }, `lane${i}`)} dir="ltr" />
              <Input disabled={d} className="h-8 text-xs" value={l.labelAr ?? ""} onChange={(e) => store.update("Lanes", (g) => { g.lanes = lanes.map((x, k) => (k === i ? { ...x, labelAr: e.target.value } : { ...x })); }, `lanear${i}`)} dir="rtl" />
            </Row>
          ))}
        </Group>
        <Group title={t("admin:builder.inspector.health")}>
          <div className="flex gap-2"><Badge variant={errors ? "danger" : "success"}>{errors} {t("admin:builder.issues.errors")}</Badge><Badge variant={warnings ? "warning" : "outline"}>{warnings} {t("admin:builder.issues.warnings")}</Badge></div>
          <ul className="space-y-1 text-[11px]">
            {issues.filter((i) => i.severity !== "Info").slice(0, 12).map((i, k) => (
              <li key={k}><button type="button" className="w-full rounded-md bg-sand-50 px-2 py-1 text-start hover:bg-sand-100" onClick={() => { const n = graph.nodes.find((x) => x.key === i.node); if (n) store.select({ nodes: [n.id!], edges: [] }); }}>{i.message}</button></li>
            ))}
          </ul>
        </Group>
        <p className="text-[11px] text-muted-foreground">{t("admin:builder.inspector.shortcuts", { mod: MOD_PREFIX })}</p>
      </div>
    </div>
  );
}
